import { useCallback, useEffect, useState } from 'react';
import { APP_SUBDOMAINS, SUBDOMAINS, subdomainFallbackUrl, subdomainUrl } from './portal';

const PROBE_TIMEOUT_MS = 3000;

// One check per subdomain per page load, shared by every link on the page.
const probes = {};
const results = {};

/**
 * Whether this visitor's network can reach https://<subdomain>.mof.gov.tl. Some ISPs (seen on
 * Timor Telecom) cannot, so links fall back to the same site on www until this says yes.
 * A no-cors request resolves for any HTTP response and rejects on DNS or connection failure.
 */
export function probeSubdomain(subdomain, fetchImpl = fetch, timeoutMs = PROBE_TIMEOUT_MS) {
    if (!probes[subdomain]) {
        probes[subdomain] = (async () => {
            const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
            const timer = controller && setTimeout(() => controller.abort(), timeoutMs);
            try {
                await fetchImpl(`${subdomainUrl(subdomain, '/favicon.ico')}?reachability=${Date.now()}`, {
                    mode: 'no-cors',
                    cache: 'no-store',
                    credentials: 'omit',
                    signal: controller?.signal,
                });
                return true;
            } catch {
                return false;
            } finally {
                if (timer) clearTimeout(timer);
            }
        })().then((ok) => {
            results[subdomain] = ok;
            return ok;
        });
    }
    return probes[subdomain];
}

/** True only if this page has already checked the subdomain and could not reach it. Makes no request. */
export const knownUnreachable = (subdomain) => results[subdomain] === false;

/** For tests: forget earlier checks. */
export function resetSubdomainProbes() {
    Object.keys(probes).forEach((key) => delete probes[key]);
    Object.keys(results).forEach((key) => delete results[key]);
}

/**
 * Returns `(subdomain, path) => url`: the subdomain address when this visitor can reach it,
 * otherwise the www fallback. Before the check finishes the fallback is used, so a link never
 * leads somewhere the visitor cannot open.
 */
export default function useSubdomainUrl() {
    const [reachable, setReachable] = useState(() => ({ ...results }));

    useEffect(() => {
        let active = true;
        // Only live subdomains are checked; the rest always use the www fallback.
        Object.entries({ ...SUBDOMAINS, ...APP_SUBDOMAINS })
            .filter(([, config]) => config.live)
            .forEach(([key]) => {
                probeSubdomain(key).then((ok) => {
                    if (active) setReachable((current) => (current[key] === ok ? current : { ...current, [key]: ok }));
                });
            });
        return () => {
            active = false;
        };
    }, []);

    return useCallback(
        (subdomain, path = '/') => (reachable[subdomain] ? subdomainUrl(subdomain, path) : subdomainFallbackUrl(subdomain, path)),
        [reachable]
    );
}
